import React, { useState, useEffect, useCallback } from 'react';
import SignalBadge from '../components/SignalBadge';
import { fetchIndicators, fetchIndices, fetchIndexContributors } from '../api';

const REFRESH_INTERVAL = 15000;

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [indices, setIndices] = useState([]);
  const [contributors, setContributors] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Contributor controls
  const [contribTab, setContribTab] = useState('all');       // 'all' | 'major' | 'gainers' | 'losers'
  const [contribSort, setContribSort] = useState('auto');     // 'auto' | 'contribution' | '-contribution' | 'weight' | 'pct_change' | '-pct_change' | 'name'
  const [contribSearch, setContribSearch] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [indRes, idxRes] = await Promise.allSettled([
        fetchIndicators(),
        fetchIndices(),
      ]);
      if (indRes.status === 'fulfilled' && indRes.value.data?.data) setData(indRes.value.data.data);
      if (idxRes.status === 'fulfilled' && idxRes.value.data?.indices) setIndices(idxRes.value.data.indices);
      // Fetch contributors separately (heavier call)
      try {
        const contribRes = await fetchIndexContributors('Nifty');
        if (contribRes.data?.contributors) setContributors(contribRes.data);
      } catch {}
      setLastRefresh(new Date().toLocaleTimeString());
    } catch {}
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [loadData]);

  const niftyChange = data ? data.nifty_price - (data.nifty_prev_close || 0) : 0;
  const niftyChangePct = data && data.nifty_prev_close > 0 ? ((niftyChange / data.nifty_prev_close) * 100) : 0;

  return (
    <div className="page-dashboard">
      {/* Page Header */}
      <div className="dash-page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Market overview and index analysis</p>
        </div>
        {lastRefresh && (
          <span className="live-indicator">
            <span className="live-pulse" /> Updated {lastRefresh}
          </span>
        )}
      </div>

      {/* Indices Ticker Strip */}
      {indices.length > 0 && (
        <section className="indices-ticker-strip">
          {indices.map((idx) => (
            <div key={idx.token} className="index-ticker-item">
              <span className="index-ticker-name">{idx.name}</span>
              <span className="index-ticker-ltp">{idx.ltp?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              <span className={`index-ticker-change ${idx.change >= 0 ? 'positive' : 'negative'}`}>
                {idx.change >= 0 ? '▲' : '▼'} {Math.abs(idx.change_pct).toFixed(2)}% ({idx.change >= 0 ? '+' : ''}{idx.change?.toFixed(1)})
              </span>
            </div>
          ))}
        </section>
      )}

      {/* Market Overview Cards */}
      {data && (
        <section className="dash-market-row">
          <div className="dash-card dash-card-nifty">
            <div className="dash-card-icon">📊</div>
            <div className="dash-card-body">
              <span className="dash-card-label">NIFTY 50</span>
              <span className="dash-card-value">{data.nifty_price?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              <span className={`dash-card-change ${niftyChange >= 0 ? 'positive' : 'negative'}`}>
                {niftyChange >= 0 ? '▲' : '▼'} {niftyChangePct.toFixed(2)}% ({niftyChange >= 0 ? '+' : ''}{niftyChange.toFixed(0)} pts)
              </span>
            </div>
          </div>
          <div className="dash-card dash-card-vix">
            <div className="dash-card-icon">📈</div>
            <div className="dash-card-body">
              <span className="dash-card-label">India VIX</span>
              <span className="dash-card-value">{data.vix_value?.toFixed(2)}</span>
              <span className={`dash-card-change ${(data.vix_change_pct || 0) >= 0 ? 'positive' : 'negative'}`}>
                {(data.vix_change_pct || 0) >= 0 ? '▲' : '▼'} {Math.abs(data.vix_change_pct || 0).toFixed(2)}%
              </span>
            </div>
          </div>
          <div className="dash-card dash-card-bias">
            <div className="dash-card-icon">🎯</div>
            <div className="dash-card-body">
              <span className="dash-card-label">Market Bias</span>
              <SignalBadge signal={data.overall_bias} />
              <span className="dash-bias-counts">
                <span className="bias-mini-b">▲ {data.bullish_count}</span>
                <span className="bias-mini-r">▼ {data.bearish_count}</span>
              </span>
            </div>
          </div>
        </section>
      )}

      {!data && (
        <div className="dash-loading-strip">
          <div className="spinner" /> Waiting for market data...
        </div>
      )}

      {/* Day Range Strip */}
      {data && (
        <section className="dash-range-strip">
          <div className="range-item">
            <span className="range-label">Day Low</span>
            <span className="range-val">{data.nifty_low?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="range-bar">
            <div className="range-bar-fill" style={{
              left: data.nifty_low && data.nifty_high && data.nifty_high !== data.nifty_low
                ? `${((data.nifty_price - data.nifty_low) / (data.nifty_high - data.nifty_low)) * 100}%`
                : '50%'
            }} />
          </div>
          <div className="range-item">
            <span className="range-label">Day High</span>
            <span className="range-val">{data.nifty_high?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="range-divider" />
          <div className="range-item">
            <span className="range-label">Range</span>
            <span className="range-val">{data.intraday_range?.toFixed(0)} pts</span>
          </div>
          <div className="range-item">
            <span className="range-label">Prev Close</span>
            <span className="range-val">{data.nifty_prev_close?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
        </section>
      )}

      {/* Index Contributors */}
      {contributors && contributors.contributors?.length > 0 && (() => {
        const allStocks = contributors.contributors || [];
        const isNeg = contributors.is_negative;
        const majorThreshold = contributors.major_threshold || 2;

        // Filter by tab
        let filtered = allStocks;
        if (contribTab === 'major') filtered = allStocks.filter(c => c.weight >= majorThreshold);
        else if (contribTab === 'gainers') filtered = allStocks.filter(c => c.pct_change > 0);
        else if (contribTab === 'losers') filtered = allStocks.filter(c => c.pct_change < 0);

        // Search filter
        if (contribSearch.trim()) {
          const q = contribSearch.trim().toLowerCase();
          filtered = filtered.filter(c => c.name.toLowerCase().includes(q) || c.symbol.toLowerCase().includes(q));
        }

        // Sort
        const sorted = [...filtered];
        if (contribSort === 'auto') {
          sorted.sort((a, b) => isNeg ? a.contribution - b.contribution : b.contribution - a.contribution);
        } else if (contribSort === 'contribution') {
          sorted.sort((a, b) => b.contribution - a.contribution);
        } else if (contribSort === '-contribution') {
          sorted.sort((a, b) => a.contribution - b.contribution);
        } else if (contribSort === 'weight') {
          sorted.sort((a, b) => b.weight - a.weight);
        } else if (contribSort === 'pct_change') {
          sorted.sort((a, b) => b.pct_change - a.pct_change);
        } else if (contribSort === '-pct_change') {
          sorted.sort((a, b) => a.pct_change - b.pct_change);
        } else if (contribSort === 'name') {
          sorted.sort((a, b) => a.name.localeCompare(b.name));
        }

        // Stats
        const filteredContrib = sorted.reduce((s, c) => s + c.contribution, 0);
        const filteredWeight = sorted.reduce((s, c) => s + c.weight, 0);
        const gainCount = allStocks.filter(c => c.pct_change > 0).length;
        const lossCount = allStocks.filter(c => c.pct_change < 0).length;

        const renderChip = (c) => (
          <div key={c.symbol} className={`contrib-chip ${c.contribution >= 0 ? 'contrib-up' : 'contrib-down'}`}>
            <div className="contrib-chip-top">
              <span className="contrib-symbol">{c.name}</span>
              <span className="contrib-weight">{c.weight}%</span>
            </div>
            <div className="contrib-chip-mid">
              <span className="contrib-ltp">{c.ltp?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              <span className={`contrib-pct ${c.pct_change >= 0 ? 'positive' : 'negative'}`}>
                {c.pct_change >= 0 ? '▲' : '▼'} {Math.abs(c.pct_change).toFixed(2)}%
              </span>
            </div>
            <div className="contrib-chip-bot">
              <span className={`contrib-pts ${c.contribution >= 0 ? 'positive' : 'negative'}`}>
                {c.contribution >= 0 ? '+' : ''}{c.contribution.toFixed(1)} pts
              </span>
            </div>
          </div>
        );

        return (
          <section className="dash-section">
            {/* Header */}
            <div className="dash-section-header">
              <h2>Nifty 50 — Stock Contributions</h2>
              <span className="contrib-index-level">
                Index: {contributors.index_level?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                {' · '}
                <span className={contributors.total_contribution >= 0 ? 'positive' : 'negative'}>
                  Net: {contributors.total_contribution >= 0 ? '+' : ''}{contributors.total_contribution?.toFixed(1)} pts
                </span>
                {' · '}
                Weight Source: {contributors.weight_source_label || 'FFMC'}
              </span>
            </div>

            {/* Toolbar: Tabs + Sort + Search */}
            <div className="contrib-toolbar">
              <div className="contrib-tabs">
                <button className={`contrib-tab ${contribTab === 'all' ? 'active' : ''}`} onClick={() => setContribTab('all')}>
                  All 50
                </button>
                <button className={`contrib-tab ${contribTab === 'major' ? 'active' : ''}`} onClick={() => setContribTab('major')}>
                  Major (≥{majorThreshold}%)
                </button>
                <button className={`contrib-tab ${contribTab === 'gainers' ? 'active' : ''}`} onClick={() => setContribTab('gainers')}>
                  <span className="positive">▲</span> Gainers ({gainCount})
                </button>
                <button className={`contrib-tab ${contribTab === 'losers' ? 'active' : ''}`} onClick={() => setContribTab('losers')}>
                  <span className="negative">▼</span> Losers ({lossCount})
                </button>
              </div>
              <div className="contrib-controls">
                <select className="contrib-sort-select" value={contribSort} onChange={e => setContribSort(e.target.value)}>
                  <option value="auto">Sort: Market Direction</option>
                  <option value="contribution">Pts: High → Low</option>
                  <option value="-contribution">Pts: Low → High</option>
                  <option value="weight">Weight: High → Low</option>
                  <option value="pct_change">% Change: High → Low</option>
                  <option value="-pct_change">% Change: Low → High</option>
                  <option value="name">Name: A → Z</option>
                </select>
                <input
                  className="contrib-search"
                  type="text"
                  placeholder="Search stock..."
                  value={contribSearch}
                  onChange={e => setContribSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Stats bar */}
            <div className="contrib-stats-bar">
              <span>{sorted.length} stocks</span>
              <span>·</span>
              <span>Weight: {filteredWeight.toFixed(1)}%</span>
              <span>·</span>
              <span className={filteredContrib >= 0 ? 'positive' : 'negative'}>
                {filteredContrib >= 0 ? '+' : ''}{filteredContrib.toFixed(1)} pts
              </span>
            </div>

            {/* Grid */}
            <div className="contrib-grid">
              {sorted.map(renderChip)}
            </div>
            {sorted.length === 0 && (
              <div className="contrib-empty">No stocks match your filters</div>
            )}
          </section>
        );
      })()}


    </div>
  );
}
